import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, RotateCcw, Sparkles, Download, TrendingUp, TrendingDown,
  Minus, AlertCircle, Info, Activity,
} from "lucide-react";
import DealScoreSparkline from "@/components/v3/DealScoreSparkline";
import { DealScoreRadar } from "@/components/v3/PropertyCharts";

/* BP-005 Deal Score tab.
   Hero score card + 5 weighted category cards + AI narrative + PDF. */

const IMPACT_COLOR = {
  positive: "text-emerald-700 bg-emerald-50 border-emerald-200",
  neutral:  "text-slate-600  bg-slate-50   border-slate-200",
  negative: "text-red-700    bg-red-50     border-red-200",
  unknown:  "text-slate-500  bg-slate-50   border-slate-200",
};

const IMPACT_ICON = {
  positive: TrendingUp, neutral: Minus, negative: TrendingDown, unknown: Minus,
};

export default function DealScoreTab({ property }) {
  const [score, setScore] = useState(null);
  const [history, setHistory] = useState([]);
  const [latestRec, setLatestRec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: sc }, cur, hist] = await Promise.all([
        api.get(`/v3/properties/${property.id}/deal-score`),
        api.get(`/v3/properties/${property.id}/ai-recommendations/current`)
          .catch(() => ({ data: { by_type: {} } })),
        api.get(`/v3/properties/${property.id}/deal-score/history`)
          .catch(() => ({ data: { items: [] } })),
      ]);
      setScore(sc);
      const preferred = cur.data.by_type?.deal_summary
                        || cur.data.by_type?.exit_strategy
                        || Object.values(cur.data.by_type || {})[0]
                        || null;
      setLatestRec(preferred);
      // Sparkline needs oldest→newest, history endpoint returns newest→oldest.
      setHistory([...(hist.data.items || [])].reverse());
    } catch (err) {
      toast.error("Failed to load Deal Score");
    } finally {
      setLoading(false);
    }
  }, [property.id]);

  useEffect(() => { load(); }, [load]);

  const calculate = useCallback(async () => {
    setCalculating(true);
    try {
      const { data } = await api.post(
        `/v3/properties/${property.id}/deal-score/calculate`, {},
      );
      setScore(data);
      toast.success(`Deal Score v${data.version} calculated — ${data.label}`);
    } catch (err) {
      toast.error("Calculation failed");
    } finally {
      setCalculating(false);
    }
  }, [property.id]);

  const runAi = useCallback(async () => {
    if (!score) return;
    setAiLoading(true);
    try {
      const { data } = await api.post(`/v3/deal-score/${score.id}/ai-review`);
      setScore(data);
      toast.success("AI analysis ready");
    } catch (err) {
      toast.error("AI review failed");
    } finally {
      setAiLoading(false);
    }
  }, [score]);

  const exportPdf = useCallback(() => {
    if (!score) return;
    window.open(
      `${process.env.REACT_APP_BACKEND_URL}/api/v3/deal-score/${score.id}/export/pdf`,
      "_blank",
    );
  }, [score]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-600 text-sm" data-testid="deal-score-loading">
        <Loader2 size={14} className="animate-spin"/> Loading Deal Score…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="tab-panel-deal_score">
      <ScoreHero
        score={score}
        history={history}
        onCalculate={calculate}
        onAi={runAi}
        onExport={exportPdf}
        calculating={calculating}
        aiLoading={aiLoading}
      />
      {score && (
        <>
          <div className="rounded-xl border border-slate-200 bg-white p-4 md:p-6" data-testid="deal-score-radar-card">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Score Radar</div>
                <div className="text-sm font-semibold text-slate-900">Where this deal shines — and where it&apos;s soft</div>
              </div>
            </div>
            <DealScoreRadar categories={score.categories} />
          </div>
          <CategoryGrid categories={score.categories}/>
          {latestRec && <LatestRecommendationCard rec={latestRec}/>}
          {score.warnings?.length > 0 && (
            <WarningsBlock warnings={score.warnings}/>
          )}
          {score.ai_review && (
            <AiBlock ai={score.ai_review}/>
          )}
        </>
      )}
      {!score && (
        <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-600">
          No Deal Score yet. Click <b>Calculate</b> above to run the composite engine.
        </div>
      )}
    </div>
  );
}

function ScoreHero({ score, history, onCalculate, onAi, onExport, calculating, aiLoading }) {
  const values = (history || []).map((h) => h.capped_score ?? h.score).filter((v) => typeof v === "number");
  const first = values.length ? values[0] : null;
  const last  = values.length ? values[values.length - 1] : null;
  const delta = (first !== null && last !== null) ? last - first : null;
  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 p-5" data-testid="deal-score-hero">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-amber-800/80">Property Deal Score</div>
          <div className="flex items-center gap-3 mt-1">
            <span className="inline-block w-4 h-4 rounded-full flex-shrink-0"
                  style={{ backgroundColor: score?.dot_hex || "#f59e0b" }}
                  data-testid="deal-score-dot"
                  aria-hidden="true"/>
            <div className="text-5xl font-bold text-slate-900 tabular-nums leading-none"
                 data-testid="deal-score-value">
              {score ? score.capped_score : "—"}
              <span className="text-2xl text-slate-500 font-medium ml-1">/100</span>
            </div>
          </div>
          <div className="text-lg font-semibold uppercase tracking-wide mt-2"
               style={{ color: score?.text_hex || "#92400e" }}
               data-testid="deal-score-label">
            {score ? score.label : "—"}
          </div>
          {score && (
            <div className="text-[11px] text-slate-600 mt-1">
              Version {score.version}
              {score.data_gap_categories?.length > 0 && (
                <span className="ml-2 text-amber-700">
                  · {score.data_gap_categories.length} category(ies) partial
                </span>
              )}
              {score.caps_applied?.length > 0 && (
                <span className="ml-2 text-red-700">
                  · capped ({score.caps_applied.join(", ")})
                </span>
              )}
            </div>
          )}
          {history && history.length >= 2 && (
            <div
              data-testid="deal-score-trend"
              className="mt-3 flex items-center gap-3 bg-white/60 rounded-lg
                         px-3 py-2 ring-1 ring-amber-200 inline-flex"
            >
              <div className="flex items-center gap-1.5 text-[10px]
                              uppercase tracking-wider text-slate-500">
                <Activity size={11}/> Trend
              </div>
              <DealScoreSparkline
                points={history}
                width={140}
                height={32}
                testid="deal-score-hero-sparkline"
              />
              <div className="text-[11px] tabular-nums">
                <span className="text-slate-500">{first}</span>
                <span className="mx-1 text-slate-400">→</span>
                <span className="font-semibold text-slate-900">{last}</span>
                {delta !== null && delta !== 0 && (
                  <span
                    data-testid="deal-score-trend-delta"
                    className={`ml-1.5 font-semibold
                                ${delta > 0 ? "text-emerald-700"
                                            : "text-red-700"}`}
                  >
                    ({delta > 0 ? "+" : ""}{delta})
                  </span>
                )}
                <span className="ml-2 text-slate-400">
                  · {values.length} versions
                </span>
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2 self-start">
          <button onClick={onCalculate} disabled={calculating}
                  data-testid="deal-score-calculate-btn"
                  className="inline-flex items-center gap-1 rounded-md bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-700 disabled:opacity-50">
            {calculating ? <Loader2 size={12} className="animate-spin"/> : <RotateCcw size={12}/>}
            Calculate
          </button>
          <button onClick={onAi} disabled={aiLoading || !score}
                  data-testid="deal-score-ai-btn"
                  className="inline-flex items-center gap-1 rounded-md border border-purple-300 bg-purple-50 text-purple-800 text-xs font-semibold px-3 py-1.5 hover:border-purple-500 disabled:opacity-40">
            {aiLoading ? <Loader2 size={12} className="animate-spin"/> : <Sparkles size={12}/>}
            AI Analysis
          </button>
          <button onClick={onExport} disabled={!score}
                  data-testid="deal-score-pdf-btn"
                  className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white text-slate-800 text-xs font-semibold px-3 py-1.5 hover:border-slate-400 disabled:opacity-40">
            <Download size={12}/> PDF
          </button>
        </div>
      </div>
    </div>
  );
}

function CategoryGrid({ categories = [] }) {
  return (
    <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-5" data-testid="deal-score-categories">
      {categories.map((cat) => <CategoryCard key={cat.id} cat={cat}/>)}
    </div>
  );
}

function CategoryCard({ cat }) {
  const pct = Math.round((cat.weight || 0) * 100);
  const subBarColor =
    cat.subscore >= 80 ? "bg-emerald-500" :
    cat.subscore >= 60 ? "bg-lime-500" :
    cat.subscore >= 40 ? "bg-amber-500" : "bg-red-500";
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`deal-score-cat-${cat.id}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">{cat.label}</div>
        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{pct}%</div>
      </div>
      <div className="flex items-baseline gap-1 mt-1">
        <div className="text-2xl font-bold text-slate-900 tabular-nums" data-testid={`deal-score-cat-${cat.id}-value`}>
          {cat.subscore}
        </div>
        <div className="text-xs text-slate-500">/100</div>
        {cat.data_gap && (
          <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-[9px] px-1.5 py-0.5 uppercase font-semibold" title="Missing input data">
            <AlertCircle size={9}/> Gap
          </span>
        )}
      </div>
      <div className="w-full h-1.5 bg-slate-100 rounded-full mt-2 overflow-hidden">
        <div className={`h-full ${subBarColor} transition-all`} style={{ width: `${cat.subscore}%` }}/>
      </div>
      <div className="mt-2 space-y-1">
        {(cat.factors || []).slice(0, 5).map((f, i) => {
          const IconEl = IMPACT_ICON[f.impact] || Minus;
          return (
            <div key={i}
                 className={`text-[10px] rounded px-1.5 py-0.5 border ${IMPACT_COLOR[f.impact] || IMPACT_COLOR.neutral}`}>
              <div className="flex items-center gap-1">
                <IconEl size={9}/>
                <span className="font-medium">{f.label}</span>
              </div>
              <div className="text-[10px] text-slate-600 truncate" title={String(f.value)}>{f.value}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WarningsBlock({ warnings }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-2" data-testid="deal-score-warnings">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">Warnings</div>
      {warnings.map((w, i) => (
        <div key={i} className="text-[12px] text-slate-800 flex items-start gap-2">
          <Info size={13} className="mt-0.5 text-amber-600"/>
          <div>
            <span className="text-[9px] px-1 py-0.5 rounded bg-slate-100 text-slate-700 uppercase font-semibold mr-2">
              {(w.severity || "info").toUpperCase()}
            </span>
            {w.message}
          </div>
        </div>
      ))}
    </div>
  );
}

function AiBlock({ ai }) {
  return (
    <div className="rounded-xl border border-purple-200 bg-purple-50/50 p-4 space-y-2" data-testid="deal-score-ai-block">
      <div className="text-[10px] uppercase tracking-wider text-purple-700">AI Analysis</div>
      <div className="text-sm text-slate-900">{ai.summary}</div>
      <div className="grid gap-2 sm:grid-cols-2 text-[12px]">
        {ai.strongestFactor && <div><b>Strongest:</b> {ai.strongestFactor}</div>}
        {ai.weakestFactor && <div><b>Weakest:</b> {ai.weakestFactor}</div>}
        {ai.nextAction && <div className="sm:col-span-2"><b>Next action:</b> {ai.nextAction}</div>}
        {ai.alternateExit && ai.alternateExit !== "none" && (
          <div className="sm:col-span-2"><b>Alternate exit:</b> {ai.alternateExit}</div>
        )}
      </div>
      {ai.dataGapsToClose?.length > 0 && (
        <div className="text-[11px] text-slate-700">
          <b>Data gaps to close:</b> {ai.dataGapsToClose.join("; ")}
        </div>
      )}
      {ai.disclaimer && <div className="text-[9px] text-slate-500 italic">{ai.disclaimer}</div>}
    </div>
  );
}

/* BP-006 compact "Latest Recommendation" summary shown on the Deal Score
   tab — the source-of-truth AI card lives on the AI Recommendations tab. */
function LatestRecommendationCard({ rec }) {
  const conf = Math.round((rec.confidence || 0) * 100);
  const statusColor = {
    complete: "bg-emerald-100 text-emerald-800 border-emerald-300",
    partial:  "bg-amber-100 text-amber-800 border-amber-300",
    blocked:  "bg-red-100 text-red-800 border-red-300",
    failed:   "bg-slate-100 text-slate-700 border-slate-300",
  }[rec.status] || "bg-slate-100 text-slate-700 border-slate-300";
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="latest-rec-card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Latest AI Recommendation</div>
          <div className="text-sm font-semibold text-slate-900 mt-0.5 truncate" title={rec.headline}>
            {rec.headline || "—"}
          </div>
          <div className="text-[11px] text-slate-600 mt-1">
            {(rec.recommendation_type || "").replace(/_/g, " ")}
            {" · "}Confidence {conf}%
            {rec.risks?.length ? ` · ${rec.risks.length} risk(s)` : ""}
          </div>
        </div>
        <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full border ${statusColor}`}>
          {rec.status}
        </span>
      </div>
      <div className="text-[10px] text-slate-500 mt-2 italic">
        Full breakdown available on the AI Recommendations tab.
      </div>
    </div>
  );
}
